import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag, User, Share2, Twitter, Linkedin, Facebook, ArrowRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useLanguage } from '../context/LanguageContext';
import { blogPosts, authors } from '../data/blogData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const BlogPostPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  const posts = blogPosts[language] || [];
  const post = posts.find(p => p.slug === slug);
  
  // Get author data
  const author = post?.authorId ? authors[post.authorId] : null;
  
  // Get related posts (same category, different post)
  const relatedPosts = posts.filter(p => p.category === post?.category && p.id !== post?.id).slice(0, 3);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">
            {language === 'es' ? 'Artículo no encontrado' : 'Article not found'}
          </h1>
          <Link to="/blog" className="text-[#F7931A] hover:underline">
            {language === 'es' ? 'Volver al blog' : 'Back to blog'}
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = window.location.href;
  const shareText = post.title;

  const textRevealVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  return (
    <main className="min-h-screen bg-[#050505] relative">
      <SEO 
        title={post.title}
        description={post.excerpt}
        image={post.image}
        url={`/blog/${post.slug}`}
        type="article"
        article={{
          date: post.date,
          author: author?.name || 'AJ Code Labs',
          category: post.category,
          tags: post.tags
        }}
      />
      <div className="grid-pattern fixed inset-0 pointer-events-none" />
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[70vh] flex items-end overflow-hidden">
        {/* Background Image with Parallax */}
        <motion.div 
          style={{ y: heroY, scale: heroScale }}
          className="absolute inset-0 z-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${post.image})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        </motion.div>

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="container-custom relative z-10 pb-16"
        >
          {/* Back Button */}
          <Link
            to="/blog"
            className="flex items-center gap-2 text-[#A1A1AA] hover:text-[#F7931A] transition-colors mb-8 group"
            data-testid="back-button"
          >
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              {language === 'es' ? 'Volver al blog' : 'Back to blog'}
            </motion.div>
          </Link>

          <div className="max-w-4xl">
            {/* Category */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-6"
            >
              <Tag className="w-4 h-4 text-[#F7931A]" />
              <span className="text-sm font-mono text-[#F7931A] tracking-wider uppercase">
                {post.category}
              </span>
            </motion.div>

            {/* Title */}
            <div className="overflow-hidden mb-6">
              <motion.h1
                custom={0}
                initial="hidden"
                animate="visible"
                variants={textRevealVariants}
                className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-white"
              >
                {post.title}
              </motion.h1>
            </div>

            {/* Excerpt */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-xl text-[#A1A1AA] leading-relaxed mb-8"
            >
              {post.excerpt}
            </motion.p>

            {/* Meta info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="flex flex-wrap items-center gap-6 text-[#A1A1AA]"
            >
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {author?.name || 'AJ Code Labs'}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-custom">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <motion.article
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-3"
            >
              <div className="prose prose-invert prose-lg max-w-none
                prose-headings:font-['Syne'] prose-headings:text-white prose-headings:font-bold
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:text-[#F7931A]
                prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-3
                prose-p:text-[#A1A1AA] prose-p:leading-relaxed
                prose-li:text-[#A1A1AA]
                prose-strong:text-white
                prose-a:text-[#F7931A] prose-a:no-underline hover:prose-a:underline
                prose-blockquote:border-l-[#F7931A] prose-blockquote:text-[#A1A1AA]
                prose-code:text-[#F7931A] prose-code:bg-white/5 prose-code:px-2 prose-code:py-1 prose-code:rounded
                prose-table:text-[#A1A1AA]
                prose-th:text-white prose-th:bg-white/5
                prose-td:border-[#27272A]
              ">
                <ReactMarkdown>{post.content}</ReactMarkdown>
              </div>

              {/* Tags */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mt-12 pt-8 border-t border-[#27272A]"
              >
                <h4 className="text-sm font-mono text-[#A1A1AA] mb-4">
                  {language === 'es' ? 'Etiquetas' : 'Tags'}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 glass rounded-full text-sm text-[#A1A1AA] hover:text-[#F7931A] hover:border-[#F7931A]/30 transition-colors cursor-default"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </motion.div>

              {/* Share */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mt-8 pt-8 border-t border-[#27272A]"
              >
                <h4 className="text-sm font-mono text-[#A1A1AA] mb-4 flex items-center gap-2">
                  <Share2 className="w-4 h-4" />
                  {language === 'es' ? 'Compartir' : 'Share'}
                </h4>
                <div className="flex gap-3">
                  <motion.a
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 glass rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#1DA1F2] hover:border-[#1DA1F2]/30 transition-colors"
                  >
                    <Twitter className="w-5 h-5" />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareText)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 glass rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#0A66C2] hover:border-[#0A66C2]/30 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </motion.a>
                  <motion.a
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 glass rounded-lg flex items-center justify-center text-[#A1A1AA] hover:text-[#1877F2] hover:border-[#1877F2]/30 transition-colors"
                  >
                    <Facebook className="w-5 h-5" />
                  </motion.a>
                </div>
              </motion.div>
            </motion.article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-8">
                {/* CTA Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="glass rounded-2xl p-6"
                >
                  <h3 className="font-['Syne'] text-lg font-bold text-white mb-3">
                    {language === 'es' ? '¿Necesitas ayuda?' : 'Need help?'}
                  </h3>
                  <p className="text-sm text-[#A1A1AA] mb-4">
                    {language === 'es' 
                      ? 'Nuestro equipo está listo para ayudarte a implementar estas soluciones.'
                      : 'Our team is ready to help you implement these solutions.'}
                  </p>
                  <Link
                    to="/#contact"
                    className="btn-primary w-full text-center text-sm py-3"
                  >
                    {language === 'es' ? 'Contactar' : 'Contact Us'}
                  </Link>
                </motion.div>

                {/* Author Card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 }}
                  className="glass rounded-2xl p-6"
                >
                  <h4 className="text-sm font-mono text-[#A1A1AA] mb-4">
                    {language === 'es' ? 'Autor' : 'Author'}
                  </h4>
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: author?.color || '#F7931A' }}
                    >
                      {author?.avatar || 'AJ'}
                    </div>
                    <div>
                      <p className="text-white font-medium">{author?.name || 'AJ Code Labs'}</p>
                      <p className="text-xs text-[#A1A1AA]">{author?.role?.[language] || 'Tech Team'}</p>
                    </div>
                  </div>
                  {author?.bio && (
                    <p className="text-sm text-[#A1A1AA] mt-4 leading-relaxed">
                      {author.bio[language]}
                    </p>
                  )}
                </motion.div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-16 border-t border-[#27272A]">
          <div className="container-custom">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-sm font-mono text-[#F7931A] tracking-widest uppercase mb-8"
            >
              {language === 'es' ? 'Artículos Relacionados' : 'Related Articles'}
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost, index) => (
                <Link key={relatedPost.id} to={`/blog/${relatedPost.slug}`}>
                  <motion.article
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -10 }}
                    className="glass rounded-2xl overflow-hidden group cursor-pointer h-full"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img 
                        src={relatedPost.image} 
                        alt={relatedPost.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-['Syne'] text-lg font-bold text-white group-hover:text-[#F7931A] transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-3 text-xs text-[#52525B]">
                        <span>{relatedPost.date}</span>
                        <span>{relatedPost.readTime}</span>
                      </div>
                    </div>
                  </motion.article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#F7931A]/10 to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-['Syne'] text-4xl md:text-5xl font-bold text-white mb-6">
              {language === 'es' 
                ? '¿Listo para dar el siguiente paso?'
                : 'Ready to take the next step?'}
            </h2>
            <p className="text-xl text-[#A1A1AA] mb-10">
              {language === 'es'
                ? 'Transformemos juntos tu negocio con tecnología de vanguardia.'
                : "Let's transform your business together with cutting-edge technology."}
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/#contact"
                className="btn-primary inline-flex items-center gap-2 text-lg px-10 py-5 group"
              >
                {language === 'es' ? 'Iniciar Proyecto' : 'Start Project'}
                <ArrowRight className="w-6 h-6 group-hover:translate-x-2 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default BlogPostPage;
